import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Building, Globe2 } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { PARTNER_GROUPS } from "@/lib/partners";
import { PartnerMark } from "@/components/PartnerMark";

const GROUP_ICONS = { psb: Landmark, private: Building, mnc: Globe2 };

const partnerSlug = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function PartnersPage() {
  useSEO({
    title: "Our Partners — Banks We Work With | Loan Salaah",
    description: "Loan Salaah sources loan and credit card offers from public sector banks, private banks and MNC banks across India. See our partner network.",
  });

  return (
    <div data-testid="partners-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Our partners</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            The network behind your offer.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            As a DSA and facilitator, Loan Salaah sources loan and credit card offers from across India's banking
            system — public sector giants, private majors and multinational banks. More partners means your
            application gets compared, not just submitted.
          </p>
        </Reveal>

        <Stagger className="mt-14 grid gap-6 lg:grid-cols-3">
          {PARTNER_GROUPS.map((g) => {
            const Icon = GROUP_ICONS[g.id] || Landmark;
            return (
              <StaggerItem key={g.id}>
                <div className="h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(20,40,75,0.14)]" data-testid={`partner-group-${g.id}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl font-semibold text-concordia">{g.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{g.note}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {g.names.map((n) => (
                      <li key={n} className="flex items-center gap-2 rounded-full border border-sand bg-cream px-4 py-1.5 text-xs font-semibold text-charcoal/80">
                        <PartnerMark
                          name={n}
                          logo={`/partners/${partnerSlug(n)}.png`}
                          imgClassName="h-4 w-auto max-w-[5rem] object-contain"
                          textClassName="hidden"
                        />
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>

        <Reveal delay={0.1}>
          <p className="mt-10 max-w-3xl text-xs leading-relaxed text-slate/80">
            Partner network is indicative and varies by product, city and current empanelment. All bank names and
            logos are the property of their respective owners; Loan Salaah acts as a Direct Selling Agent /
            facilitator and does not represent itself as any of these institutions.
          </p>
          <div className="relative mt-10 overflow-hidden rounded-[2rem] bg-concordia p-10 text-ivory sm:p-12" data-testid="partners-cta">
            <div className="noise-overlay" aria-hidden="true" />
            <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div>
                <h2 className="font-serif text-2xl font-semibold sm:text-3xl">One enquiry. The whole network.</h2>
                <p className="mt-2 max-w-md text-sm text-ivory/70">Tell us what you need — we compare across our partners and call you back with your strongest options.</p>
              </div>
              <Link to="/apply" data-testid="partners-apply-button"
                className="group inline-flex items-center gap-2 rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.3)]">
                Apply Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
